import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, useTheme, CircularProgress, Card } from '@mui/material';
import OpenComponentDialog from 'src/components/shared-components/modal/openComponentModal';
import PatientDatas from 'src/modules/medcard/patient-datas';
import Menu from './components/menu';
import GridMenu from './components/grid-menu';
import CalendarBoard from './components/calendar-board';
import WaitingMenu from './components/waiting-menu';
import ReturnMenu from './components/return-menu';
import { useTranslate } from 'src/locales';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import type { CardItemVariant } from './components/card-item';
import { useBaseQuery } from 'src/api/core/useBaseQuery';
import McList from 'src/modules/medcard/general-health-information/components/MC-LIST';
import ByzMuiTypography from 'src/components/core-components/mui/inputs/ByzMuiTypography';
import ByzMuiCloseButton from 'src/components/core-components/mui/buttons/ByzMuiCloseButton';
import { mcGeneralDiseaseService } from 'src/api/medcards/mcGeneralDiseaseService';
import ByzGrdEditButton from 'src/components/core-components/mui/buttons-grid/ByzGrdEditButton';
import GeneralHealthInformationModal from 'src/modules/medcard/general-health-information';
import type {
  TreatmentApplicationsModalProps,
  PatientOrderAndOtherTreatmentDetail,
  PatientOrderAndOtherTreatmentResponse,
} from './models';

type CategoryKey = 'all' | 'medicine' | 'other' | 'blood';

const TreatmentApplicationsModal: React.FC<TreatmentApplicationsModalProps> = ({
  open,
  onClose,
  patientId,
  upn,
  patientName,
  gender,
  isPregnant,
  initialCategory,
  initialDate,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  // Responsive kolon sayısı: mobil 1, tablet 2, desktop 4
  const visibleColumnCount = isMobile ? 1 : isTablet ? 2 : 4;

  const [selectedTab, setSelectedTab] = useState('order');
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey>(initialCategory || 'all');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate || new Date());
  const [generalDiseasesData, setGeneralDiseasesData] = useState<{
    counts: Record<string, number>;
    groups: any[];
  } | null>(null);
  const [isGhiModalOpen, setIsGhiModalOpen] = useState(false);
  const [ghiInitialSection, setGhiInitialSection] = useState<string | undefined>(undefined);
  const [localIsPregnant, setLocalIsPregnant] = useState<boolean | undefined>(isPregnant);
  const [localUpn, setLocalUpn] = useState<string | number | undefined>(upn);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // MC-LIST refresh için

  // Update local state when prop changes
  useEffect(() => {
    if (isPregnant !== undefined) {
      setLocalIsPregnant(isPregnant);
    }
  }, [isPregnant]);
  useEffect(() => {
    if (upn !== undefined) {
      setLocalUpn(upn);
    }
  }, [upn]);
  // Modal her açıldığında state'i tazele
  useEffect(() => {
    if (!open) return;

    setSelectedTab('order');
    setSelectedFilter('all');
    setSelectedCategory(initialCategory || 'all');
    setSelectedDate(initialDate || new Date());
  }, [open, initialCategory, initialDate]);


  const getTypeFromCategory = (category: string): number => {
    switch (category) {
      case 'medicine':
        return 2; // Order
      case 'other':
        return 3; // Diğer Tedaviler
      case 'blood':
        return 4; // Kan İstekleri
      default:
        return 1; // Tümü
    }
  };

  // API endpoint'i oluştur - kategori değişince API'ye istek at
  const apiEndpoint = useMemo(() => {
    if (!patientId || !selectedDate) return '';

    const type = getTypeFromCategory(selectedCategory);
    const searchDate = dayjs(selectedDate).format('YYYY.MM.DD');

    return `/nurse/api/Order/GetPatientOrderAndOtherTreatments?patientId=${patientId}&type=${type}&SearchDate=${searchDate}`;
  }, [patientId, selectedDate, selectedCategory]);

  // API çağrısı - kategori değişince API'ye istek at, cache yok her seferinde yeni istek
  const { data: apiData, isLoading, error, refetch } = useBaseQuery<PatientOrderAndOtherTreatmentResponse[]>({
    endpoint: apiEndpoint,
    queryKey: ['patient-order-treatments', patientId, selectedDate, selectedCategory],
    options: {
      enabled: !!apiEndpoint && selectedTab === 'order', // Sadece order tab'ında ve endpoint varsa çağır
      staleTime: 0, // Veri hemen eski olarak işaretlenir
      gcTime: 0, // Cache süresi 0 (React Query v5)
      refetchOnMount: 'always', // Mount olduğunda her zaman yeniden fetch
      refetchOnWindowFocus: false, // Window focus'ta refetch yapma
      refetchOnReconnect: false, // Reconnect'te refetch yapma
    },
  });

  // Highlight slot index'ini hesaplayan fonksiyon
  const calculateHighlightSlotIndex = (date: Date | null) => {
    const now = dayjs();
    const baseDay = dayjs(date || new Date()).startOf('day');
    if (now.isSame(baseDay, 'day')) {
      // Şu anki saatten sonraki ilk 2 saatlik slotu bul
      const stepHours = 2;
      const currentHour = now.hour();
      const currentMinute = now.minute();

      // Şu anki slotu bul (örn: 14:56 -> 14:00 slotu)
      const currentSlotHour = Math.floor(currentHour / stepHours) * stepHours;

      // Eğer şu anki saat tam slotun başındaysa (dakika = 0) ve saat de slot başındaysa, o slotu al
      // Aksi halde bir sonraki slotu al
      let targetHour: number;
      if (currentMinute === 0 && currentHour === currentSlotHour) {
        // Tam slot başındayız, o slotu al
        targetHour = currentSlotHour;
      } else {
        // Slotun içindeyiz veya geçtik, bir sonraki slotu al
        const nextSlotHour = currentSlotHour + stepHours;
        targetHour = nextSlotHour >= 24 ? 22 : nextSlotHour;
      }

      // Target hour'un index'ini bul (00:00 = 0, 02:00 = 1, ..., 22:00 = 11)
      return targetHour / stepHours;
    }
    return 2; // Bugün değilse ortada başla (default: 04:00)
  };

  // Highlight slot index - tarih değiştiğinde güncellenir
  const [highlightSlotIndex, setHighlightSlotIndex] = useState(() =>
    calculateHighlightSlotIndex(selectedDate)
  );

  // Mavi slotun index'ini tut (başlangıçta 0, API'den gelen kolon sayısına göre ayarlanacak)
  const [centerSlotIndex, setCenterSlotIndex] = useState(0);

  // Tarih değiştiğinde highlight ve center slot index'lerini güncelle
  useEffect(() => {
    const newHighlightIndex = calculateHighlightSlotIndex(selectedDate);
    setHighlightSlotIndex(newHighlightIndex);
    setCenterSlotIndex(newHighlightIndex);
  }, [selectedDate]);

  // Şimdiki zamanı takip etmek için periyodik tick (highlight'ın saatte kayması için)
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setNowTick(Date.now());
    }, 60_000); // her dakika güncelle

    return () => clearInterval(timer);
  }, []);

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category as CategoryKey);
  };

  const handleFilterChange = (filter: string) => {
    setSelectedFilter(filter);
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  // Genel Sağlık Bilgileri özet kartı için veriyi çek
  useEffect(() => {
    const fetchGeneralDiseases = async () => {
      if (!patientId || !open) return;

      try {
        const response = await mcGeneralDiseaseService.getMcGeneralDiseases(patientId);
        setGeneralDiseasesData({
          counts: response.counts,
          groups: response.groups,
        });
      } catch (error) {
        setGeneralDiseasesData(null);
      }
    };

    fetchGeneralDiseases();
  }, [patientId, open, refreshTrigger]); // refreshTrigger: modal kapanınca veriyi yenile

  const handlePrevBoard = () => {
    setCenterSlotIndex((prev) => {
      // Sola basınca index azalır, minimum 0
      return Math.max(0, prev - 1);
    });
  };

  const handleNextBoard = () => {
    setCenterSlotIndex((prev) => {
      // Sağa basınca index artar
      // Maksimum değer API'den gelen kolon sayısına bağlı
      // useMemo içinde hesaplanacak ama şimdilik sınır yok
      return prev + 1;
    });
  };

  // API datayı boardColumns formatına dönüştür - sadece veri olan saatlerde kolonlar
  const transformApiDataToBoardColumns = (
    apiData: PatientOrderAndOtherTreatmentResponse[] | undefined,
    category: string,
    filter: string,
    selectedDate: dayjs.Dayjs
  ) => {
    if (!apiData) {
      return [];
    }

    // Backend bazen doğrudan dizi, bazen { data: [...] } döndürüyor
    let list: PatientOrderAndOtherTreatmentResponse[] = [];

    if (Array.isArray(apiData)) {
      list = apiData;
    } else if ((apiData as any) && Array.isArray((apiData as any).data)) {
      list = (apiData as any).data as PatientOrderAndOtherTreatmentResponse[];
    } else {
      console.warn('[TreatmentApplicationsModal] apiData has unexpected shape, skipping transform:', apiData);
      return [];
    }

    // API datayı plandate ve planhourshow'a göre grupla
    const itemsBySlot: { [key: string]: { plandate: string; planhourshow: string; items: PatientOrderAndOtherTreatmentDetail[] } } = {};

    list.forEach((item) => {
      const plandate = dayjs(item.plandate);
      const planhourshow = item.planhourshow; // "00", "02", "11", "14" gibi

      // planhourshow'u saat olarak parse et
      const hour = parseInt(planhourshow, 10);
      if (isNaN(hour)) return;

      // Her saat için ayrı kolon oluştur (slotlara bölme)
      const slotKey = `${plandate.format('YYYY-MM-DD')}-${hour}`;

      if (!itemsBySlot[slotKey]) {
        itemsBySlot[slotKey] = {
          plandate: item.plandate,
          planhourshow: planhourshow,
          items: [],
        };
      }

      // Her detail'i ekle
      item.patientorderandothertreatmentdetail.forEach((detail) => {
        itemsBySlot[slotKey].items.push(detail);
      });
    });

    const sortedSlotKeys = Object.keys(itemsBySlot).sort((a, b) => {
      const [dateA, hourA] = a.split('-');
      const [dateB, hourB] = b.split('-');
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      return parseInt(hourA, 10) - parseInt(hourB, 10);
    });

    const allCols = [];

    for (let i = 0; i < sortedSlotKeys.length; i++) {
      const slotKey = sortedSlotKeys[i];
      const slotData = itemsBySlot[slotKey];

      if (!slotData) continue;

      const plandateObj = dayjs(slotData.plandate);
      const hour = parseInt(slotData.planhourshow, 10);

      if (isNaN(hour)) continue;

      const time = plandateObj.hour(hour).minute(0);

      // Türkçe ay ismi ile label oluştur: "23 ARALIK 11:00"
      const day = plandateObj.date();
      const monthName = plandateObj.locale('tr').format('MMMM').toUpperCase();
      const label = `${day} ${monthName}  ${time.format('HH:mm')}`;

      // Seçili tarihten farklı tarih mi kontrol et
      const isDifferentDate = !plandateObj.isSame(selectedDate, 'day');

      // Bu slot için items oluştur
      const slotItems = slotData.items;
      const planhourshow = slotData.planhourshow; // Slot'un planhourshow değeri
      const transformedItems = slotItems
        .map((detail, idx) => {
          let cardType: CardItemVariant = 'medicine';
          let title = '';
          let itemId = '';
          let itemTime = time.format('HH:mm');
          let planHourDate: dayjs.Dayjs | null = null;

          if (detail.orderdetail) {
            const order = detail.orderdetail;
            itemId = `order-${order.srumid}-${order.seqno}-${idx}`;
            title = (order.reqmatname?.trim() || order.matname?.trim() || '');

            // planhour'dan saat bilgisini al - orderdetail'in kendi planhour'u varsa onu kullan
            if (order.planhour) {
              planHourDate = dayjs(order.planhour);
              itemTime = planHourDate.format('HH:mm');
            } else {
              // planhour yoksa planhourshow'dan saat bilgisini al
              const hour = parseInt(planhourshow, 10);
              if (!isNaN(hour)) {
                itemTime = `${hour.toString().padStart(2, '0')}:00`;
              }
            }

            // ismixture kontrolü - "0" veya "1" string olarak gelebilir
            if (order.ismixture === '1' || String(order.ismixture) === '1') {
              cardType = 'medicine-mix';
            } else {
              cardType = 'medicine';
            }
          } else if (detail.othertreatment) {
            const otherTreatment = detail.othertreatment;
            itemId = `other-${otherTreatment.udid}-${idx}`;
            title = otherTreatment.name || otherTreatment.srntype || 'Diğer Tedavi';

            // isblood kontrolüne göre tip belirle
            if (otherTreatment.isblood === '1' || String(otherTreatment.isblood) === '1') {
              cardType = 'blood';
            } else {
              cardType = 'other';
            }

            // startdate'den saat bilgisini al veya planhourshow'u kullan
            if (otherTreatment.startdate) {
              planHourDate = dayjs(otherTreatment.startdate);
              itemTime = planHourDate.format('HH:mm');
            }
          }

          return { id: itemId, time: itemTime, title, cardType, rawData: detail, planHourDate };
        })
        .filter((item) => {
          if (category !== 'all') {
            if (category === 'medicine' && item.cardType !== 'medicine' && item.cardType !== 'medicine-mix') return false;
            if (category === 'other' && item.cardType !== 'other') return false;
            if (category === 'blood' && item.cardType !== 'blood') return false;
          }
          if (filter === 'all') return true;

          // rawData'dan orderdetail bilgisini al
          const orderDetail = item.rawData?.orderdetail;
          const now = dayjs();

          // OtherTreatment (kan / diğer tedaviler) için filtreleme
          if (!orderDetail && item.rawData?.othertreatment) {
            const ismake = item.rawData.othertreatment.ismake;
            const isApplied = ismake === '1' || String(ismake) === '1';
            switch (filter) {
              case 'pending':
                // Beklemede: henüz uygulanmamış ve plan saati şimdi veya sonrasında
                return !isApplied && (!item.planHourDate || !item.planHourDate.isBefore(now));
              case 'applied':
                return isApplied;
              case 'past':
                // Geçenler: henüz uygulanmamış ve plan saati şu andan önce
                return !isApplied && item.planHourDate ? item.planHourDate.isBefore(now) : false;
              default: return true;
            }
          }

          if (!orderDetail) return true;

          const state = orderDetail.state;

          // state mapping: 0 = beklemede/uygulanmamış, 1 = uygulanmış
          switch (filter) {
            case 'pending':
              // Beklemede: henüz uygulanmamış ve plan saati şimdi veya sonrasında
              return state === 0 && (!item.planHourDate || !item.planHourDate.isBefore(now));
            case 'applied':
              return state === 1;
            case 'past':
              // Geçenler: henüz uygulanmamış ve plan saati şu andan önce
              return state === 0 && item.planHourDate ? item.planHourDate.isBefore(now) : false;
            default: return true;
          }
        });

      if (transformedItems.length > 0) {
        allCols.push({
          id: `col-${slotKey}`,
          label,
          items: transformedItems,
          globalIndex: i,
          isDifferentDate: !plandateObj.isSame(selectedDate, 'day'),
          slotDateTime: time.toISOString(),
        });
      }
    }
    return allCols;
  };


  // Sadece veri olan kolonları göster
  const { boardColumns, highlightFromIndex } = useMemo(() => {
    const baseDay = dayjs(selectedDate || new Date()).startOf('day');

    // Tüm kolonları oluştur (filtreleme yapmadan)
    const allCols = transformApiDataToBoardColumns(
      apiData,
      selectedCategory,
      selectedFilter,
      baseDay
    );

    if (allCols.length === 0) {
      return { boardColumns: [] as any[], highlightFromIndex: -1 };
    }

    // Bugünkü "aktif" 2 saatlik slotun global index'ini bul
    const now = dayjs(nowTick);
    const today = now.startOf('day');

    const stepHours = 2;
    const currentHour = now.hour();
    const currentMinute = now.minute();
    const currentSlotHour = Math.floor(currentHour / stepHours) * stepHours;

    let targetHour: number;
    if (currentMinute === 0 && currentHour === currentSlotHour) {
      targetHour = currentSlotHour;
    } else {
      const nextSlotHour = currentSlotHour + stepHours;
      targetHour = nextSlotHour >= 24 ? 22 : nextSlotHour;
    }

    let activeGlobalIndex = -1;

    allCols.forEach((col: any, idx: number) => {
      if (!col.slotDateTime) return;
      const colTime = dayjs(col.slotDateTime);
      if (!colTime.isSame(today, 'day')) return;
      if (colTime.hour() === targetHour) {
        activeGlobalIndex = idx;
      }
    });

    // Eğer aktif slot bulunamadıysa, mevcut centerSlotIndex'i kullan
    const baseIndex = activeGlobalIndex >= 0 ? activeGlobalIndex : centerSlotIndex;

    // Slice başlangıcını ayarla: aktif kolon mümkünse 2. veya 3. sırada olsun
    const maxStart = Math.max(0, allCols.length - visibleColumnCount);
    const desiredOffset = visibleColumnCount >= 4 ? 1 : 0; // 4 kolon görünürken aktif olan 2. sırada olsun
    let startIdx = baseIndex - desiredOffset;
    startIdx = Math.max(0, Math.min(startIdx, maxStart));

    const endIdx = Math.min(allCols.length, startIdx + visibleColumnCount);
    const cols = allCols.slice(startIdx, endIdx);

    // Highlight index: slice içindeki aktif kolonun local index'i
    const finalHighlightIndex =
      activeGlobalIndex >= 0
        ? cols.findIndex((c: any) => c.id === allCols[activeGlobalIndex].id)
        : -1;

    return { boardColumns: cols, highlightFromIndex: finalHighlightIndex };
  }, [selectedDate, centerSlotIndex, visibleColumnCount, apiData, selectedCategory, selectedFilter, nowTick]);


  // API verisinden srnids ve srumids listelerini topla
  const { srnids, srumids, isIdentityVerified } = useMemo(() => {
    const srnidSet = new Set<number>();
    const srumidSet = new Set<number>();
    let hasAnyOrder = false;
    let hasUnverifiedOrder = false;

    if (apiData) {
      let list: PatientOrderAndOtherTreatmentResponse[] = [];

      if (Array.isArray(apiData)) {
        list = apiData;
      } else if ((apiData as any) && Array.isArray((apiData as any).data)) {
        list = (apiData as any).data as PatientOrderAndOtherTreatmentResponse[];
      } else {
        console.warn('[TreatmentApplicationsModal] apiData is not an array in identity extractor, skipping:', apiData);
        return {
          srnids: [],
          srumids: [],
          isIdentityVerified: false,
        };
      }

      list.forEach((item) => {
        item.patientorderandothertreatmentdetail.forEach((detail) => {
          if (detail.orderdetail?.srumid) {
            srumidSet.add(detail.orderdetail.srumid);
            hasAnyOrder = true;

            const authenticator = String((detail.orderdetail as any)?.authenticator ?? '').trim();
            const authenticationtime = (detail.orderdetail as any)?.authenticationtime;
            const isVerified = !!authenticator || !!authenticationtime;
            if (!isVerified) {
              hasUnverifiedOrder = true;
            }
          }
          if (detail.othertreatment?.srnid) {
            srnidSet.add(detail.othertreatment.srnid);
            hasAnyOrder = true;

            const otAuthenticator = String((detail.othertreatment as any)?.authenticator ?? '').trim();
            const otAuthenticationTime = (detail.othertreatment as any)?.authenticationtime;
            const otIsVerified = !!otAuthenticator || !!otAuthenticationTime;
            if (!otIsVerified) {
              hasUnverifiedOrder = true;
            }
          }
        });
      });
    }

    return {
      srnids: Array.from(srnidSet),
      srumids: Array.from(srumidSet),
      isIdentityVerified: hasAnyOrder && !hasUnverifiedOrder,
    };
  }, [apiData]);

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '90vw', md: '87vw' },
          maxWidth: { xs: '95vw', sm: '90vw', md: '87vw' },
        },
      }}
      hideHeader={true}
      showPatientData={false}
    >
      <Box
        sx={{
          height: 'auto',
          minHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'height 0.3s ease-in-out',
          position: 'relative',
        }}
      >
        {/* Loading Overlay - Sayfanın ortasında */}
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 9999,
              backdropFilter: 'blur(2px)',
            }}
          >
            <CircularProgress size={48} sx={{ color: '#2979C3' }} />
          </Box>
        )}
        {/* Başlık */}
        <Box
          sx={{
            width: '100%',
            backgroundColor: '#f4f7fe',
            borderBottom: '1px solid #e0e0e0',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: { xs: 1, sm: 1.25, md: 1.5 },
            px: { xs: 1.5, sm: 2, md: 2.5 },
            mb: { xs: 1.5, sm: 1.75, md: 2 },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: 16, sm: 18, md: 20 },
              lineHeight: { xs: '20px', sm: '21px', md: '22px' },
              fontWeight: 600,
              color: '#111827',
            }}
          >
            {t('nurse.patient-list.detail-grid.treatment-applications')}
          </Typography>
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        {/* Hasta bilgisi kartı */}
        <Box sx={{ mb: { xs: 1.5, sm: 1.75, md: 2 }, px: { xs: 1, sm: 1.5, md: 2 } }}>
          <PatientDatas
            patientId={patientId}
            isCreateMedCard
            isOpenMedCard
            patientDataOut={(data: any) => {
              if (data && data.data) {
                // Check pregnancy from detailed patient data
                const isPregnantFromApi = data.data.pregnantorlactation === "D";
                // Also check gender from API to cover corner cases
                const genderFromApi = data.data.sexuality;

                // If we found pregnancy status from API, use it
                if (isPregnantFromApi) {
                  setLocalIsPregnant(true);
                }

                // API'den gelen upn veya protid bilgisini al
                const upnFromApi = data.data.upn || data.data.protid || data.data.protId;
                if (upnFromApi && (!localUpn || localUpn === 0 || localUpn === "0")) {
                  setLocalUpn(upnFromApi);
                }

                if (genderFromApi) {
                }
              }
            }}
          />
        </Box>

        {/* Genel Sağlık Bilgileri - MC-LIST */}
        <Box sx={{ mb: { xs: 1.5, sm: 1.75, md: 2 }, px: { xs: 1, sm: 1.5, md: 2 } }}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: '0 20px 45px rgba(15,23,42,0.08)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
              <ByzMuiTypography variant="h6" sx={{ fontWeight: 600 }}>
                {t('patient-diet-procedures.ghi.general-health-information')}
              </ByzMuiTypography>
              <ByzGrdEditButton onClick={() => {
                setGhiInitialSection(undefined);
                setIsGhiModalOpen(true);
              }} />
            </Box>
            <McList
              patientId={patientId}
              generalDiseasesData={generalDiseasesData}
              gender={gender}
              isPregnant={localIsPregnant}
              refreshTrigger={refreshTrigger}
              onOpenGhiModal={(section) => {
                setGhiInitialSection(section);
                setIsGhiModalOpen(true);
              }}
            />
          </Card>
        </Box>

        {/* İçerik alanı */}
        <Box
          sx={{
            flex: 1,
            px: { xs: 1, sm: 1.5, md: 2 },
            pb: { xs: 1.5, sm: 1.75, md: 2 },
            overflow: 'auto',
          }}
        >
          {/* Menu */}
          <Box sx={{ mb: { xs: 1.5, sm: 1.75, md: 2 } }}>
            <Menu
              selectedTab={selectedTab}
              onTabChange={handleTabChange}
              patientName={patientName}
              srnids={srnids}
              srumids={srumids}
              isIdentityVerified={isIdentityVerified}
              onRefetch={refetch}
            />
          </Box>

          {/* Grid Menu - Sadece order tab'ında göster */}
          {selectedTab === 'order' && (
            <Box sx={{ mb: { xs: 1.5, sm: 1.75, md: 2 } }}>
              <GridMenu
                selectedCategory={selectedCategory}
                selectedFilter={selectedFilter}
                selectedDate={selectedDate}
                onCategoryChange={handleCategoryChange}
                onFilterChange={handleFilterChange}
                onDateChange={handleDateChange}
                isLoading={isLoading}
              />
            </Box>
          )}

          {/* Tab Content */}
          <Box>
            {selectedTab === 'order' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <CalendarBoard
                  columns={boardColumns}
                  onPrev={handlePrevBoard}
                  onNext={handleNextBoard}
                  highlightFromIndex={highlightFromIndex}
                  patientId={patientId}
                  upn={localUpn}
                  refetch={refetch}
                />
              </Box>
            )}
            {selectedTab === 'pending' && <WaitingMenu patientId={patientId} />}
            {selectedTab === 'return' && <ReturnMenu patientId={patientId} />}
          </Box>
        </Box>
      </Box>

      <GeneralHealthInformationModal
        open={isGhiModalOpen}
        onClose={() => {
          setIsGhiModalOpen(false);
          setGhiInitialSection(undefined);
          // MC-LIST'i refresh et
          setRefreshTrigger(prev => prev + 1);
        }}
        patientId={patientId}
        patientName={patientName}
        initialSection={ghiInitialSection}
      />
    </OpenComponentDialog>
  );
};

export default TreatmentApplicationsModal;


